// Staging access gate (outer layer) — client helpers.
//
// A single SHARED password that keeps the whole staging environment private.
// This is SEPARATE from the app's real per-user auth (vat_admin_token) and
// never touches it. The staging token is stored under its own key and sent
// on every request via the X-Staging-Access header (matching the app's
// header-token posture). When STAGING_MODE is off on the backend, the gate
// status reports gate_active=false and this layer is fully transparent.
import axios from "axios";
import { API } from "./backend";
import { installWriteGenerations } from "./sharedRead";

const STAGING_KEY = "vat_staging_access";

export const getStagingToken = () => localStorage.getItem(STAGING_KEY);
export const setStagingToken = (t) => localStorage.setItem(STAGING_KEY, t);
export const clearStagingToken = () => localStorage.removeItem(STAGING_KEY);

// Fired when a request comes back {staging_locked:true} so the gate can
// re-render without a full reload loop.
export const STAGING_LOCKED_EVENT = "vat:staging-locked";

const bare = axios.create({ baseURL: API, timeout: 15000 });
installWriteGenerations(bare);   // A1-95: see lib/sharedRead.js

export const fetchStagingStatus = async () => {
  const { data } = await bare.get("/staging/status", {
    headers: stagingHeaders(),
  });
  return data; // {staging_mode, gate_active, unlocked}
};

export const unlockStaging = async (password) => {
  const { data } = await bare.post("/staging/unlock", { password });
  if (data?.token) setStagingToken(data.token);
  return data; // {ok, token} | throws on 401
};

export const stagingHeaders = () => {
  const t = getStagingToken();
  return t ? { "X-Staging-Access": t } : {};
};

// Install the staging header + locked-detection on an axios instance.
export const installStagingInterceptors = (instance) => {
  instance.interceptors.request.use((cfg) => {
    const t = getStagingToken();
    if (t) cfg.headers["X-Staging-Access"] = t;
    return cfg;
  });
  instance.interceptors.response.use(
    (r) => r,
    (e) => {
      if (e?.response?.status === 401 && e.response.data?.staging_locked) {
        clearStagingToken();
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event(STAGING_LOCKED_EVENT));
        }
      }
      return Promise.reject(e);
    },
  );
};
